<div class="page-container">
  <div class="flex flex-col gap-8">
    <app-page-header
      [title]="headerTitle()"
      [description]="headerDescription()" />

    <!-- Create with AI -->
    <button
      type="button"
      class="group relative flex w-full cursor-pointer flex-col gap-4 overflow-hidden rounded-[1.5rem] bg-slate-900 px-6 py-7 text-left text-white transition hover:bg-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-8"
      (click)="startBuilding()">
      <div class="pointer-events-none absolute inset-0 opacity-40"
        style="background: radial-gradient(circle at 15% 20%, rgb(255 255 255 / 0.18), transparent 45%), radial-gradient(circle at 90% 80%, rgb(255 255 255 / 0.1), transparent 40%);">
      </div>

      <div class="relative z-10 flex flex-col gap-3 sm:max-w-xl">
        <div class="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
          <img
            src="assets/icons/brand/ai.svg"
            width="14"
            height="14"
            alt=""
            aria-hidden="true"
            class="h-3.5 w-3.5" />
          AI Form Builder
        </div>
        <h2 class="text-xl font-semibold tracking-tight sm:text-2xl">
          Create a form with AI
        </h2>
        <p class="text-sm leading-6 text-white/70">
          Describe what you need in plain language. We'll generate fields, structure, and a live preview you can refine.
        </p>
      </div>

      <div class="relative z-10 inline-flex items-center gap-2 self-start rounded-2xl bg-white px-5 py-3 text-sm font-medium text-slate-900 transition group-hover:gap-3">
        Start building
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 12h12M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
    </button>

    <!-- Stats -->
    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="rounded-[1.25rem] bg-white p-5">
        <div class="flex items-center justify-between gap-2">
          <p class="text-sm text-neutral-600">Total Forms</p>
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
            aria-hidden="true">
            <app-svg-icon src="assets/icons/view-agenda-outline.svg" [size]="20" />
          </span>
        </div>
        <p class="mt-0.5 text-h4 font-medium text-neutral-950">{{ totalForms() }}</p>
      </div>
      <div class="rounded-[1.25rem] bg-white p-5">
        <div class="flex items-center justify-between gap-2">
          <p class="text-sm text-neutral-600">Published</p>
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"
            aria-hidden="true">
            <app-svg-icon src="assets/icons/check-circle-outline.svg" [size]="20" />
          </span>
        </div>
        <p class="mt-0.5 text-h4 font-medium text-neutral-950">{{ publishedCount() }}</p>
      </div>
      <div class="rounded-[1.25rem] bg-white p-5">
        <div class="flex items-center justify-between gap-2">
          <p class="text-sm text-neutral-600">Drafts</p>
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600"
            aria-hidden="true">
            <app-svg-icon src="assets/icons/square-edit-outline.svg" [size]="20" />
          </span>
        </div>
        <p class="mt-0.5 text-h4 font-medium text-neutral-950">{{ draftCount() }}</p>
      </div>
      <div class="rounded-[1.25rem] bg-primary-600 p-5">
        <div class="flex items-center justify-between gap-2">
          <p class="text-sm text-primary-100">Responses</p>
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white"
            aria-hidden="true">
            <app-svg-icon src="assets/icons/chart-bar.svg" [size]="20" />
          </span>
        </div>
        <p class="mt-0.5 text-h4 font-medium text-white">{{ responsesCount() }}</p>
      </div>
    </div>

    <!-- Recent forms -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between gap-3">
        <h2 class="text-base font-semibold text-neutral-950">Recent forms</h2>
        <a
          routerLink="/forms"
          class="text-sm font-medium text-primary-700 transition hover:text-primary-800">
          View all
        </a>
      </div>

      @if (listState.loading() && forms().length === 0) {
        <div class="grid gap-4 md:grid-cols-3">
          @for (_ of [1, 2, 3]; track $index) {
            <div class="animate-pulse rounded-[1.25rem] bg-white p-5">
              <div class="h-4 w-2/3 rounded bg-slate-100"></div>
              <div class="mt-6 flex items-center justify-between">
                <div class="h-3 w-20 rounded bg-slate-100"></div>
                <div class="h-3 w-16 rounded bg-slate-100"></div>
              </div>
            </div>
          }
        </div>
      } @else if (recentForms().length === 0) {
        <div class="rounded-[1.25rem] bg-white px-5 py-10 text-center">
          <p class="text-sm font-medium text-neutral-950">No forms yet</p>
          <p class="mt-1 text-sm text-neutral-500">
            Create your first form to see it here.
          </p>
        </div>
      } @else {
        <div class="grid gap-4 md:grid-cols-3">
          @for (form of recentForms(); track form.id) {
            <a
              [routerLink]="formLink(form)"
              class="flex cursor-pointer flex-col gap-4 rounded-[1.25rem] bg-white p-5 transition hover:bg-slate-50">
              <div class="flex items-start justify-between gap-3">
                <h3 class="text-sm font-medium text-neutral-950">{{ form.name }}</h3>
                <span
                  class="inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize"
                  [class]="getStatusClass(form.status)">
                  {{ form.status }}
                </span>
              </div>
              <div class="flex items-center justify-between text-xs text-neutral-500">
                <span>0 responses</span>
                <span>{{ formatRelativeUpdatedAt(form.updatedAt) }}</span>
              </div>
            </a>
          }
        </div>
      }
    </div>
  </div>
</div>
